
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Card } from 'primereact/card';
import { SplitButton } from 'primereact/splitbutton';
import { Dialog } from 'primereact/dialog';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Checkbox } from "primereact/checkbox";
import { Tag } from 'primereact/tag';

// Services e Hooks
import { dashboardAPI } from '../services/dashboardAPI';
import { importControlAPI } from '../services/importControlAPI';
import { notificationAPI } from '../services/notificationAPI';
import FilterBar from '../components/FilterBar';
import StatusCard, { StatusSummary } from '../components/StatusCard';
import UploadModal from '../components/UploadModal';
import useFilters from '../hooks/useFilters';
import { de } from 'date-fns/locale';

const Dashboard = ({ toastRef }) => {
  const [configs, setConfigs] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});

  const [loading, setLoading] = useState(false);
  const [showGlobalUpload, setShowGlobalUpload] = useState(false);
  const [selectedDataKey, setSelectedDataKey] = useState(null);

  // Status dos itens filtrados
  const [stats, setStats] = useState({
    SUCCESS: 0, FAILED: 0, PENDING: 0, RECEIVED: 0, PROCESSING: 0, MANUAL_PENDING: 0, EXPORTED: 0
  });

  // Status globais (total do banco)
  const [globalStats, setGlobalStats] = useState({
    SUCCESS: 0, FAILED: 0, PENDING: 0, RECEIVED: 0, PROCESSING: 0, MANUAL_PENDING: 0, EXPORTED: 0
  });

  const [fullscreenTable, setFullscreenTable] = useState(false);
  const [expandAllRows, setExpandAllRows] = useState(false);

  const [defaultExpandApplied, setDefaultExpandApplied] = useState(false);

  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [selectedErrorMessage, setSelectedErrorMessage] = useState('');
  const [fullscreenLayoutData, setFullscreenLayoutData] = useState(null);
  const [showMissingUpload, setShowMissingUpload] = useState(false);

  const [resetConfirmVisible, setResetConfirmVisible] = useState(false);
  const [resetConfirmImportId, setResetConfirmImportId] = useState(null);

  // Hook de filtros unificado
  const { filters, setFilters } = useFilters({
    page: 1,
    limit: 10,
    enterprise_name: null,
    layout_name: null,
    status: null
  });

  // 1. GERADOR DINÂMICO DE MESES E COLUNAS
  const { monthOptions } = useMemo(() => {
    const opts = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const labelLong = d.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
      opts.push({ label: labelLong, value: monthKey });
    }
    return { monthOptions: opts };
  }, []);

  // 2. FUNÇÕES AUXILIARES E CHAMADAS DE API (DECLARAÇÕES)
  const processResponse = useCallback((response) => {
    const processedData = (response.data || []).map(config => ({
      ...config,
      layouts: (config.layouts || []).map(layout => ({
        ...layout,
        historyMap: (layout.history || []).reduce((acc, curr) => {
          acc[curr.month] = {
            status: curr.status,
            error: curr.error_message,
            file_path: curr.file_path,
            tab_temp: curr.tab_temp
          };
          return acc;
        }, {})
      }))
    }));

    setConfigs(processedData);
    setTotalRecords(response.pagination?.total || 0);
    if (response?.count) setStats(response?.count);
  }, []);

  // Carregamento dos registros com filtro
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardAPI.list(filters);
      processResponse(response);
    } catch (error) {
      toastRef.current?.show({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar dados' });
    } finally {
      setLoading(false);
    }
  }, [filters, toastRef, processResponse]);

  // Carregamento dos status globais (Total Geral por status do endpoint /stats)
  const loadGlobalStats = useCallback(async () => {
    try {
      const response = await dashboardAPI.getStats();
      if (response?.data) {
        setGlobalStats(response.data);
      } else if (response) {
        setGlobalStats(response);
      }
    } catch (error) {
      console.error('Erro ao buscar stats globais:', error);
    }
  }, []);

  // Polling: carrega dados sem setar loading
  const pollConfigs = useCallback(async () => {
    try {
      const response = await dashboardAPI.list(filters);
      processResponse(response);
    } catch {
      // Silencia erros no polling para não incomodar o usuário
    }
  }, [filters, processResponse]);

  // 3. EFEITOS (DECLARADOS APÓS AS FUNÇÕES ACIMA)
  // Listener do postMessage do PHP parent
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'openMissingModal') {
        setShowMissingUpload(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Carga inicial dos dados
  useEffect(() => {
    loadConfigs();
    loadGlobalStats();
  }, [loadConfigs, loadGlobalStats]);

  // POLLING: atualiza os dados a cada 5 segundos
  const pollingRef = useRef(null);
  const isPollingRef = useRef(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Aba invisível: pausa o polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } else {
        // Aba visível: reinicia o polling
        pollingRef.current = setInterval(async () => {
          if (isPollingRef.current) return;
          isPollingRef.current = true;
          try {
            await pollConfigs();
          } finally {
            isPollingRef.current = false;
          }
        }, 5000);
      }
    };

    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pollConfigs]);

  // Expansão inicial padrão
  useEffect(() => {
    if (defaultExpandApplied) return;
    if (!configs || configs.length === 0) return;

    const all = {};
    (configs || []).forEach((row) => {
      all[row.id] = true;
    });

    setExpandedRows(all);
    setExpandAllRows(true);
    setDefaultExpandApplied(true);
  }, [configs, defaultExpandApplied]);

  // 4. AÇÕES DA TELA
  const handleAction = (monthKey, rowData) => {
    setSelectedDataKey({ monthKey, rowData });
    setShowGlobalUpload(true);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handleStatusCardClick = (statusValue) => {
    handleFilterChange({ status: statusValue });
  };

  const handleResetProcess = async (importId) => {
    try {
      setLoading(true);
      const response = await importControlAPI.resetProcessFile(importId);
      if (response.success) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Processo reiniciado com sucesso.'
        });
        loadConfigs();
        loadGlobalStats();
      }
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível reiniciar o processo.'
      });
      console.error('Reprocess error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openResetConfirm = (importId) => {
    setResetConfirmImportId(importId);
    setResetConfirmVisible(true);
  };

  const confirmResetProcess = async () => {
    const importId = resetConfirmImportId;
    setResetConfirmVisible(false);
    if (!importId) return;
    await handleResetProcess(importId);
  };

  const cancelResetProcess = () => {
    setResetConfirmVisible(false);
    setResetConfirmImportId(null);
  };

  const handleReprocess = async (importId) => {
    try {
      setLoading(true);
      const response = await importControlAPI.reprocessFile(importId);
      if (response.success) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Arquivo reprocessado com sucesso.'
        });
        loadConfigs();
        loadGlobalStats();
      }
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível reprocessar o arquivo.'
      });
      console.error('Reprocess error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (filePathBase64) => {
  try {
    setLoading(true);
debugger;
    // 1. Chama a API Node para obter a URL assinada do S3/Spaces
    const response = await importControlAPI.downloadFile(filePathBase64);

    // Extrai a signedUrl da resposta (suportando Axios `response.data` ou fetch direto)
    const signedUrl = response.data?.signedUrl || response.signedUrl;

    if (!signedUrl) {
      throw new Error('URL assinada não encontrada na resposta.');
    }

    // 2. Cria um elemento <a> temporário para disparar o download no navegador
    const link = document.createElement('a');
    link.href = signedUrl;
    
    // A propriedade target='_blank' garante que o navegador abra/baixe sem bloquear
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    toastRef.current?.show({
      severity: 'error',
      summary: 'Erro',
      detail: 'Não foi possível baixar o arquivo.'
    });
    console.error('Download error:', error);
  } finally {
    setLoading(false);
  }
};

  const openErrorModal = (message) => {
    setSelectedErrorMessage(message || 'Erro não informado');
    setErrorModalVisible(true);
  };

  const generateToken = useCallback((importId, cod_emp, tab_temp) => {
    const CryptoJS = require('crypto-js');

    const header = btoa(JSON.stringify({ alg: 'HS256' }));
    const now = Math.floor(Date.now() / 1000);

    const payload = btoa(JSON.stringify({
      import_id: importId,
      cod_emp: cod_emp,
      tab_temp: tab_temp,
      exp: now + 3600
    }));

    const unsigned = `${header}.${payload}`;
    const secret = 'ls-import-control-secret';

    const sigWords = CryptoJS.HmacSHA256(unsigned, secret);
    const sigBase64 = CryptoJS.enc.Base64.stringify(sigWords);

    const signature = sigBase64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');

    return Promise.resolve(`${unsigned}.${signature}`);
  }, []);

  // 5. TEMPLATES DE CÉLULAS E TABELAS
  const statusCellTemplate = (rowData, monthKey) => {
    const historyData = rowData.historyMap?.[monthKey];
    if (!historyData) return null; // Não desenha nada se não houver histórico gravado
    const status = historyData.status;

    // Se houver filtro de status ativo e o status do mês atual não for correspondente, não renderiza o botão
    if (filters?.status) {
      const activeFilter = filters.status === 'EC_EXPORTED' ? 'EXPORTED' : filters.status;
      if (status !== activeFilter) {
        return null;
      }
    }

    const errorMessage = historyData?.error;
    const filePath = historyData?.file_path;
    const importId = rowData.history?.find(h => h.month === monthKey)?.id;
    const cod_emp = rowData.cod_emp;
    const tab_temp = historyData?.tab_temp;
    const isEContador = (rowData?.layout_export === "RUZ" && status === 'EXPORTED');

    const statusConfigs = {
      'SUCCESS': { label: 'Exportar', severity: 'info' },
      'FAILED': { label: 'Erro', severity: 'danger' },
      'PENDING': { label: 'Pendente', severity: 'secondary' },
      'PROCESSING': { label: 'Processando', severity: 'warning' },
      'MANUAL_PENDING': { label: 'Informar', severity: 'warning' },
      'RECEIVED': { label: 'Recebido', severity: 'help' },
      'EXPORTED': { label: 'Exportado', severity: 'success' }
    };

    const config = statusConfigs[status] || statusConfigs['PENDING'];
    const showReceiveButton = (status === 'PENDING');
    const showProcessButton = (status === 'PROCESSING');
    const showReceiveButtonFailed = (status === 'FAILED');
    const showDownloadButton = (status === 'RECEIVED');
    const showDownloadButtonResult = (status === 'SUCCESS' || status === 'MANUAL_PENDING' || status === 'EC_EXPORTED' || status === 'EXPORTED');

    const model = [
      ...(showDownloadButtonResult ? [{
        label: 'Visualizar resultados',
        icon: "pi pi-eye",
        command: async () => {
          if (!importId) return;
          const token = await generateToken(importId, cod_emp, tab_temp);
          window.parent.postMessage({
            type: 'SHOW_RESULTS',
            token: token
          }, '*');
          toastRef.current?.show({ severity: 'info', summary: 'Sucesso', detail: 'Janela de resultados solicitada' });
        }
      }] : []),
      ...(showReceiveButtonFailed || showProcessButton ? [{
        label: 'Reprocessar o arquivo',
        icon: status === 'FAILED' ? "pi pi-refresh" : "pi pi-download",
        command: () => handleReprocess(importId)
      }] : []),
      ...(showReceiveButtonFailed || showReceiveButton || showDownloadButton ? [{
        label: 'Receber Arquivo',
        icon: "pi pi-file-import",
        command: () => handleAction(monthKey, rowData)
      }] : []),
      ...(filePath ? [{
        label: 'Baixar Arquivo',
        icon: "pi pi-download",
        command: () => handleDownload(filePath)
      }] : []),
      ...(status === 'FAILED' && errorMessage ? [{
        label: 'Ver erro',
        icon: "pi pi-exclamation-circle",
        command: () => openErrorModal(errorMessage)
      }] : []),
      ...(!showReceiveButton ? [{
        label: 'Resetar o processo',
        icon: "pi pi-sync",
        command: () => openResetConfirm(importId)
      }] : []),
    ];

    const colorMapping = {
      'info': '#3b82f6',
      'danger': '#ef4444',
      'secondary': '#64748b',
      'warning': '#f59e0b',
      'help': '#a855f7',
      'success': '#22c55e'
    };

    const currentColor = colorMapping[config.severity] || '#64748b';
    const targetColor = isEContador ? '#22c55e' : currentColor;

    const customStatusClass = `custom-split-${status}-${monthKey}`;

    return (
      <div className="flex align-items-center gap-2 w-full">
        <style>{`
        .${customStatusClass}.p-splitbutton {
          border: 1px solid ${targetColor} !important;
          background-color: transparent !important;
          border-radius: 6px !important;
        }
        .${customStatusClass} .p-button {
          background-color: transparent !important;
          color: ${targetColor} !important;
          border: none !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          padding: 0.2rem 0.2rem !important; 
        }
        .${customStatusClass} .p-button .p-button-label,
        .${customStatusClass} .p-button .p-button-icon {
          color: ${targetColor} !important;
          font-size: 14px !important;
        }
        .${customStatusClass} .p-splitbutton-menubutton {
          border-left: none !important;
        }

        .p-tieredmenu .p-menuitem-link {
          padding: 0.4rem 0.6rem !important;
        }
        .p-tieredmenu .p-menuitem-text,
        .p-tieredmenu .p-menuitem-icon {
          font-size: 14px !important;
        }
      `}</style>

        <SplitButton
          label={isEContador ? 'eContador' : config.label}
          icon={isEContador ? "pi pi-cloud" : ""}
          model={model}
          menuStyle={{ minWidth: '160px' }}
          className={customStatusClass}
          style={{
            height: '32px',
            minWidth: '150px',
            borderRadius: '6px',
            overflow: 'hidden',
          }}
          pt={{
            button: { className: 'bg-transparent' },
            menuButton: { className: 'bg-transparent' }
          }}
        />
      </div>
    );
  };

  // Reenvio de e-mail
  const [resendConfirmVisible, setResendConfirmVisible] = useState(false);
  const [resendConfirmConfigId, setResendConfirmConfigId] = useState(null);

  const openResendConfirm = (configId) => {
    setResendConfirmConfigId(configId);
    setResendConfirmVisible(true);
  };

  const confirmResendEmail = async () => {
    const configId = resendConfirmConfigId;
    setResendConfirmVisible(false);

    if (!configId) return;

    try {
      setLoading(true);
      const response = await notificationAPI.resendEmail(configId);

      if (response?.success) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'E-mail reenviado com sucesso.'
        });
        loadConfigs();
      } else {
        toastRef.current?.show({
          severity: 'warn',
          summary: 'Atenção',
          detail: response?.message || 'Não foi possível reenviar o e-mail.'
        });
      }
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível reenviar o e-mail.'
      });
      console.error('ResendEmail error:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelResendEmail = () => {
    setResendConfirmVisible(false);
    setResendConfirmConfigId(null);
  };

  const emailCellTemplate = (rowData) => {
    const status = rowData.last_email_status;
    const sentAt = rowData.last_email_sent_at ? new Date(rowData.last_email_sent_at).toLocaleDateString('pt-BR') : '';

    const statusConfigs = {
      'SENT': { label: 'Enviada', severity: 'success', icon: 'pi pi-check' },
      'FAILED': { label: 'Falhou', severity: 'danger', icon: 'pi pi-times' },
      'PENDING': { label: 'Aguardando', severity: 'warning', icon: 'pi pi-clock' }
    };

    const currentStatus = statusConfigs[status] || { label: 'Não enviada', severity: 'secondary', icon: 'pi pi-envelope', style: { backgroundColor: '#64748b', color: '#ffffff' } };

    return (
      <div className="flex align-items-center gap-2">
        <Tag
          value={currentStatus.label}
          severity={currentStatus.severity}
          variant="outlined"
          icon={currentStatus.icon}
          style={{ fontSize: '12px', ...currentStatus.style }}
        />
        {sentAt && (
          <Button
            icon="pi pi-refresh"
            className="p-button p-button-text p-button-sm"
            style={{ height: '15px' }}
            tooltip="Reenviar e-mail"
            tooltipOptions={{ position: 'top' }}
            onClick={() => openResendConfirm(rowData.id)}
          />
        )}
      </div>
    );
  };

  const rowExpansionTemplate = (data) => {
    const historyMonths = Array.from(
      new Set(
        (data.layouts || [])
          .flatMap((l) => (l.history || []).map((h) => h.month))
      )
    ).filter(Boolean);

    historyMonths.sort((a, b) => (a < b ? 1 : -1));

    const filteredHistoryMonths = historyMonths.filter((monthKey) => {
      return (data.layouts || []).some((layout) => {
        const historyMap = layout?.historyMap || {};
        const historyData = historyMap[monthKey];

        // 1. Regra base da Opção B: se não existe registro gravado no banco para este mês, ignora
        if (!historyData) return false;

        // 2. Se houver filtro de status ativo, valida se o status bate com o filtro
        if (filters?.status) {
          const targetStatus = filters.status === 'EC_EXPORTED' ? 'EXPORTED' : filters.status;

          // Se for filtro do eContador, valida também o layout_export === 'RUZ'
          if (filters.status === 'EC_EXPORTED') {
            return layout.layout_export === 'RUZ' && historyData.status === 'EXPORTED';
          }

          return historyData.status === targetStatus;
        }

        // 3. Se não houver filtro de status ativo e o mês existe no banco, exibe a coluna
        return true;
      });
    });

    const formatMonthHeader = (monthKey) => {
      const [yearStr, monthStr] = String(monthKey).split('-');
      const year = Number(yearStr);
      const monthIndex = Number(monthStr) - 1;
      if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return monthKey;
      const d = new Date(year, monthIndex, 1);
      const labelShort = d.toLocaleString('pt-BR', { month: 'short' }).toUpperCase().replace('.', '');
      return `${labelShort}-${year}`;
    };

    return (
      <div className="p-3 bg-gray-50 border-round shadow-inner" style={{ overflowX: "auto", maxWidth: "100%" }}>
        <DataTable
          value={data.layouts}
          scrollable
          responsiveLayout="scroll"
          emptyMessage="Sem dados para apresentar."
          className="p-datatable-sm shadow-1 border-round overflow-hidden"
          tableStyle={{ minWidth: 'auto', justifycontent: 'start', width: 'auto' }}
        >
          <Column
            field="fantasy_name"
            header="Empresa - Layout"
            body={(rowData) => (
              <div>
                <i style={{ marginRight: '10px' }} className="pi pi-list" />
                <span className="text-sm font-medium text-gray-700">
                  {rowData.fantasy_name}
                </span>
              </div>
            )}
            frozen
            alignFrozen="left"
            style={{ zIndex: 1, minWidth: '250px', width: '250px', fontWeight: 'bold', color: '#4b5563' }}
            className="pl-3"
          />

          {filteredHistoryMonths.map((monthKey) => (
            <Column
              key={monthKey}
              header={formatMonthHeader(monthKey)}
              body={(rowData) => statusCellTemplate(rowData, monthKey)}
              style={{ width: '3rem' }}
              headerClassName="text-left border-bottom-1 text-sm"
              className="text-left"
            />
          ))}
        </DataTable>
      </div>
    );
  };

  return (
    <div className={`flex flex-column gap-3 p-2 bg-gray-100 h-screen overflow-hidden ${fullscreenTable ? 'p-0 bg-transparent' : ''}`}>

      {/* Modal de Detalhes de Erro */}
      <Dialog
        visible={errorModalVisible}
        onHide={() => setErrorModalVisible(false)}
        modal
        closable={false}
        style={{ width: '100%', maxWidth: '550px' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
      >
        <div className="flex flex-column bg-white">
          <div className="flex align-items-start gap-2 mb-4">
            <div className="flex align-items-center justify-content-center bg-red-50 text-red-500 border-circle surface-overlay shadow-1" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem' }} />
            </div>
            <div className="flex flex-column gap-1 pt-1">
              <h3 className="m-0 text-xl font-bold text-gray-900">Falha no Processamento</h3>
              <p className="m-0 text-sm text-gray-500 line-height-3">
                Ocorreu um erro crítico ao tentar importar ou processar o arquivo selecionado. Detalhes técnicos abaixo:
              </p>
            </div>
          </div>

          <div className="surface-50 border-1 border-200 border-round-lg p-3 mb-4">
            <ScrollPanel style={{ width: '100%', height: '220px' }} className="custom-scroll">
              <pre
                className="m-0 text-xs text-gray-800 line-height-3 whitespace-pre-wrap font-monospace selection:bg-red-200"
                style={{ fontFamily: 'var(--font-family, monospace)' }}
              >
                {selectedErrorMessage}
              </pre>
            </ScrollPanel>
          </div>

          <div className="flex justify-content-end gap-2 border-top-1 border-100 pt-3">
            <Button
              label="Fechar"
              icon="pi pi-times"
              onClick={() => setErrorModalVisible(false)}
              className="p-button-outlined p-button-danger p-button-sm border-round-md font-semibold px-3"
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      {/* Modal de Confirmação de Reset */}
      <Dialog
        visible={resetConfirmVisible}
        onHide={cancelResetProcess}
        modal
        closable={false}
        style={{ width: '100%', maxWidth: '550px' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
      >
        <div className="flex flex-column bg-white">
          <div className="flex align-items-start gap-2 mb-4">
            <div className="flex align-items-center justify-content-center bg-yellow-50 text-yellow-600 border-circle surface-overlay shadow-1" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem' }} />
            </div>
            <div className="flex flex-column gap-1 pt-1">
              <h3 className="m-0 text-xl font-bold text-gray-900">Confirmar reset</h3>
              <p className="m-0 text-sm text-gray-500 line-height-3">
                Se confirmar a ação os dados vão ser perdidos.
              </p>
            </div>
          </div>

          <div className="flex justify-content-end gap-2 border-top-1 border-100 pt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={cancelResetProcess}
              className="p-button-outlined p-button-secondary p-button-sm border-round-md font-semibold px-3"
            />
            <Button
              label="Confirmar"
              icon="pi pi-check"
              onClick={confirmResetProcess}
              className="p-button-danger p-button-sm border-round-md font-semibold px-3"
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      {/* Modal de Confirmação de Reenvio de E-mail */}
      <Dialog
        visible={resendConfirmVisible}
        onHide={cancelResendEmail}
        modal
        closable={false}
        style={{ width: '100%', maxWidth: '550px' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
      >
        <div className="flex flex-column bg-white">
          <div className="flex align-items-start gap-2 mb-4">
            <div className="flex align-items-center justify-content-center bg-blue-50 text-blue-600 border-circle surface-overlay shadow-1" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
              <i className="pi pi-envelope" style={{ fontSize: '1.5rem' }} />
            </div>
            <div className="flex flex-column gap-1 pt-1">
              <h3 className="m-0 text-xl font-bold text-gray-900">Reenviar e-mail</h3>
              <p className="m-0 text-sm text-gray-500 line-height-3">
                Confirmar o reenvio do e-mail para este cliente?
              </p>
            </div>
          </div>

          <div className="flex justify-content-end gap-2 border-top-1 border-100 pt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={cancelResendEmail}
              className="p-button-outlined p-button-secondary p-button-sm border-round-md font-semibold px-3"
            />
            <Button
              label="Confirmar"
              icon="pi pi-check"
              onClick={confirmResendEmail}
              className="p-button-primary p-button-sm border-round-md font-semibold px-3"
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      {/* Cards de Status (Total Filtrado e Total Geral) */}
      {!fullscreenTable && (
        <div className="flex-shrink-0">
          <StatusSummary
            stats={stats}
            value={globalStats}
            onStatusClick={handleStatusCardClick}
          />
        </div>
      )}

      {/* FilterBar */}
      <div className="bg-white border-round-lg shadow-1 p-3 flex-shrink-0">
        <FilterBar filters={filters} onFilterChange={handleFilterChange} />
      </div>

      {/* Card da Tabela principal */}
      <Card
        className={`shadow-2 border-round-xl overflow-hidden flex flex-column flex-1 ${fullscreenTable ? 'w-full h-full' : ''}`}
        pt={{
          body: { className: 'p-3 flex flex-column flex-1 h-full overflow-hidden' },
          content: { className: 'p-0 flex flex-column flex-1 h-full overflow-hidden' }
        }}
        style={{ marginBottom: '35px' }}
      >
        <div className="flex items-start justify-between mb-2 flex-shrink-0">
          <div className="flex items-center">
            <Checkbox
              inputId="expander"
              name="expandir"
              onChange={(e) => {
                const checked = e.target.checked;
                setExpandAllRows(checked);
                if (!checked) {
                  setExpandedRows({});
                  return;
                }
                const all = {};
                (configs || []).forEach((row) => {
                  all[row.id] = true;
                });
                setExpandedRows(all);
              }}
              checked={expandAllRows}
            />
            <label htmlFor="expander" className="ml-2 font-medium text-sm">Expandir todos</label>
          </div>

          <div className="flex items-start justify-end">
            <Button
              type="button"
              icon={fullscreenTable ? 'pi pi-window-minimize' : 'pi pi-window-maximize'}
              className="p-button-text p-button-sm"
              onClick={() => setFullscreenTable(prev => !prev)}
              aria-label={fullscreenTable ? 'Sair do modo amplo' : 'Entrar no modo amplo'}
            />
          </div>
        </div>

        {/* Container flexível para a tabela e o paginador */}
        <div className="flex flex-column flex-1 h-full min-h-0 overflow-hidden">
          <DataTable
            value={configs}
            lazy
            paginator
            emptyMessage="Sem dados para apresentar."
            rows={filters.limit}
            first={(filters.page - 1) * filters.limit}
            totalRecords={totalRecords}
            onPage={(e) => setFilters(prev => ({ ...prev, page: e.page + 1, limit: e.rows }))}
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={rowExpansionTemplate}
            dataKey="id"
            loading={loading}
            responsiveLayout="scroll"
            scrollable
            scrollHeight="flex"
            tableStyle={{ minWidth: '100%', tableLayout: 'fixed' }}
            className="p-datatable-sm flex-1 flex flex-column justify-content-between h-full overflow-hidden"
            pt={{
              wrapper: { className: 'flex-1 overflow-auto' },
              paginator: { className: 'mt-auto border-top-1 surface-border flex-shrink-0' }
            }}
          >
            <Column expander style={{ width: '3rem' }} />
            <Column
              body={() => null}
              style={{ width: 0, padding: 0, border: 'none' }}
            />

            <Column
              field="enterprise_name"
              header="Cliente"
              className="text-sm font-medium text-gray-700"
              body={(rowData) => (
                <div>
                  <i style={{ marginRight: '10px' }} className="pi pi-building" />
                  <span className="font-semibold text-slate-900 dark:text-slate-900 text-md">
                    {rowData.enterprise_name}
                  </span>
                </div>
              )}
            />
            <Column
              field="enterprise_email"
              className="text-sm font-medium text-gray-700"
              header="Email de Contato"
              body={(rowData) => (
                <div>
                  {rowData.enterprise_email}
                </div>
              )}
            />
            <Column header="Notificação" body={emailCellTemplate} style={{ width: '240px' }} />
          </DataTable>
        </div>
      </Card>

      <UploadModal
        visible={showGlobalUpload}
        onHide={() => setShowGlobalUpload(false)}
        onUploadComplete={() => {
          loadConfigs();
          loadGlobalStats();
        }}
        dataKey={selectedDataKey}
        monthList={monthOptions}
      />
    </div>
  );
};

export default Dashboard;